import React, { useEffect, useRef } from 'react';
import { Spin, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUnapprovedPaymentsAction,
  updateUnsettledPaymentsAction,
} from '../../Loan/store/loanActions';
import ApprovalCustomerDetails, {
  type ApprovalCustomer,
} from '../ApprovalCustomerDetails';

interface LoanPayment {
  loanNumber: string | undefined;
  cashier: string;
  transactionDate: string;
  description: string;
  notes?: string;
  branch?: any;
  paymentType: string;
  _id: string;
  interestAmount: number;
  principal: number | undefined;
  OtherTransaction?: {
    Amount: number;
    Description: string;
  };
  paymentMethod?: string;
  customerId?: string;
  reducingInterest: number;
  customer?: ApprovalCustomer;
  // jewel id -> how it left the vault; only a release (loan closure) carries it
  loanClosureType?: Record<string, string>;
}

export default function LoanPaymentsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { unapprovedPayments }: any = useSelector<any>((state) => state?.loan);

  const processingRef = useRef(false);
  const getLoanPayments = async (): Promise<void> => {
    await getUnapprovedPaymentsAction(dispatch, {});
    processingRef.current = false;
  };

  useEffect(() => {
    void getLoanPayments();
  }, []);

  const handleUpdate = async (loanPayment: LoanPayment, status: string) => {
    // if (processingRef.current) return;
    // processingRef.current = true;
    try {
      await updateUnsettledPaymentsAction(dispatch, {
        id: loanPayment._id,
        status,
      });
      void getLoanPayments();
    } catch (error) {
      console.error('Error updating payment:', error);
      processingRef.current = false;
    } finally {
      processingRef.current = false;
    }
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: LoanPayment, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.branch?.name}</p>
      ),
    },
    {
      title: 'Loan Number',
      dataIndex: 'loanNumber',
      key: 'loanNumber',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.loanNumber}</p>
      ),
    },
    {
      title: 'Customer Details',
      dataIndex: 'customer',
      key: 'customer',
      render: (_: any, record: LoanPayment) => (
        <ApprovalCustomerDetails customer={record.customer} />
      ),
    },
    {
      title: 'Payment Type',
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (_: any, record: LoanPayment) => (
        // paymentType 'full' only means the interest due was cleared, not that the loan closed
        <p>
          {Object.keys(record.loanClosureType ?? {}).length > 0
            ? 'Release'
            : 'Interest'}
        </p>
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.principal}</p>
      ),
    },
    {
      title: 'Interest',
      dataIndex: 'interest',
      key: 'interest',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.interestAmount}</p>
      ),
    },
    {
      title: 'Reducing Interest',
      dataIndex: 'reducingInterest',
      key: 'reducingInterest',
      render: (_: any, record: LoanPayment, index: number) => (
        <p>{record.reducingInterest}</p>
      ),
    },
    {
      title: 'Accept',
      dataIndex: 'actions',
      width: 200,
      render: (_: any, record: LoanPayment, index: number) => (
        <button
          className="search__button"
          style={{
            background: !processingRef.current ? '#27ae60' : '#b2b2b2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 24px',
            fontWeight: 700,
            cursor: !processingRef.current ? 'pointer' : 'not-allowed',
            opacity: !processingRef.current ? 1 : 0.7,
          }}
          disabled={processingRef.current}
          onClick={() => {
            if (!processingRef.current) {
              processingRef.current = true;
              void handleUpdate(record, 'cash_settlement');
            }
          }}
        >
          Accept
        </button>
      ),
    },
    {
      title: 'Reject',
      dataIndex: 'actions2',
      width: 200,
      render: (_: any, record: LoanPayment, index: number) => (
        <button
          className="search__button"
          style={{
            background: !processingRef.current ? '#e74c3c' : '#b2b2b2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 24px',
            fontWeight: 700,
            cursor: !processingRef.current ? 'pointer' : 'not-allowed',
            opacity: !processingRef.current ? 1 : 0.7,
          }}
          disabled={processingRef.current}
          onClick={() => {
            if (!processingRef.current) {
              processingRef.current = true;
              void handleUpdate(record, 'rejected');
            }
          }}
        >
          Reject
        </button>
      ),
    },
  ];

  return (
    <div className="users__table__page">
      {!(unapprovedPayments.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table dataSource={unapprovedPayments} columns={columns} />
      </div>
    </div>
  );
}
